<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import TimeAxis from './grid/TimeAxis.vue';
import EventBlock from './grid/EventBlock.vue';
import NowLine from './grid/NowLine.vue';

const props = defineProps({
  date: { type: String, required: true },
  appointments: { type: Array, default: () => [] },
  businessHours: {
    type: Object,
    default: () => ({ start: '08:00', end: '20:00' }),
  },
});
const emit = defineEmits(['open-appointment', 'createAt']);

const PIXELS_PER_HOUR = 60;

const store = useStore();

const statusFilter = computed(
  () => store.getters['painelView/getStatusFilter']
);

const filteredAppointments = computed(() =>
  props.appointments.filter(a => statusFilter.value.has(a.status))
);

function assignLanes(appts) {
  const sorted = appts
    .slice()
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));

  const result = sorted.map(appt => ({ appt, lane: 0, laneCount: 1 }));

  // First pass: assign lanes using greedy column packing
  for (let i = 0; i < result.length; i += 1) {
    const curr = result[i];
    const currStart = new Date(curr.appt.starts_at).getTime();
    const currEnd = new Date(curr.appt.ends_at).getTime();

    const usedLanes = new Set();
    for (let j = 0; j < i; j += 1) {
      const prev = result[j];
      const prevStart = new Date(prev.appt.starts_at).getTime();
      const prevEnd = new Date(prev.appt.ends_at).getTime();
      if (currStart < prevEnd && currEnd > prevStart) {
        usedLanes.add(prev.lane);
      }
    }

    let lane = 0;
    while (usedLanes.has(lane) && lane < 2) lane += 1;
    curr.lane = lane;
  }

  // Second pass: set laneCount = max lane in each overlap cluster + 1 (capped at 3)
  for (let i = 0; i < result.length; i += 1) {
    const curr = result[i];
    const currStart = new Date(curr.appt.starts_at).getTime();
    const currEnd = new Date(curr.appt.ends_at).getTime();

    const cluster = result.filter(other => {
      const oStart = new Date(other.appt.starts_at).getTime();
      const oEnd = new Date(other.appt.ends_at).getTime();
      return currStart < oEnd && currEnd > oStart;
    });

    const maxLane = Math.max(...cluster.map(o => o.lane));
    const laneCount = Math.min(maxLane + 1, 3);
    cluster.forEach(o => {
      o.laneCount = Math.max(o.laneCount, laneCount);
    });
  }

  return result;
}

const appointmentsWithLanes = computed(() =>
  assignLanes(filteredAppointments.value)
);

const startHour = computed(() =>
  Number(props.businessHours.start.split(':')[0])
);
const endHour = computed(() => Number(props.businessHours.end.split(':')[0]));

const isToday = computed(
  () => props.date === new Date().toLocaleDateString('en-CA')
);

const scrollContainer = ref(null);

const scrollToNow = (smooth = true) => {
  if (!isToday.value || !scrollContainer.value) return;
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes() - startHour.value * 60;
  const target = Math.max(0, minutes * (PIXELS_PER_HOUR / 60) - 80);
  scrollContainer.value.scrollTo({
    top: target,
    behavior: smooth ? 'smooth' : 'auto',
  });
};

const onColumnClick = e => {
  const rect = e.currentTarget.getBoundingClientRect();
  const y = e.clientY - rect.top;
  const totalMinutes = (y / PIXELS_PER_HOUR) * 60 + startHour.value * 60;
  const snapped = Math.round(totalMinutes / 15) * 15;
  emit('createAt', { startMinutes: snapped });
};

const columnStyle = computed(() => ({
  height: `${(endHour.value - startHour.value) * PIXELS_PER_HOUR}px`,
}));

onMounted(() => scrollToNow(false));
watch(scrollContainer, el => {
  if (el && isToday.value) scrollToNow(false);
});
watch(
  () => props.date,
  () => scrollToNow(true)
);
</script>

<template>
  <div class="flex flex-1 flex-col overflow-hidden bg-n-surface-1">
    <div ref="scrollContainer" class="relative flex-1 overflow-y-auto">
      <div class="flex">
        <TimeAxis :start-hour="startHour" :end-hour="endHour" />
        <div
          class="relative flex-1 border-l border-n-slate-3"
          :style="columnStyle"
          @click="onColumnClick"
        >
          <EventBlock
            v-for="item in appointmentsWithLanes"
            :key="item.appt.id"
            :appointment="item.appt"
            professional-color="#3b82f6"
            :start-hour="startHour"
            :pixels-per-hour="PIXELS_PER_HOUR"
            :lane="item.lane"
            :lane-count="item.laneCount"
            @open="a => emit('open-appointment', a)"
          />
        </div>
      </div>
      <NowLine v-if="isToday" :start-hour="startHour" />
    </div>
  </div>
</template>
